<script>
import debounce from "debounce";
import Spinner from "@/components/ui/Spinner";
import pagination from "@/mixins/pagination";
import ModalProgram from "@/components/settings/program/ModalProgram.vue";
import programService from "@/services/program";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "ProgramList",

  mixins: [pagination],

  components: {
    Spinner,
    ModalProgram,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      programs: [],
      isProgramModalVisible: false,
      selectedProgram: {},
    };
  },

  created() {
    this.getPrograms();

    this.$bus.$on("program-created", (program) => {
      this.programs.unshift(program);
    });

    this.$bus.$on("program-updated", (updatedProgram) => {
      const index = this.programs.findIndex(
        (p) => p._id === updatedProgram._id
      );
      if (index !== -1) {
        this.$set(this.programs, index, updatedProgram);
      }
    });
  },

  beforeDestroy() {
    this.$bus.$off("program-created");
    this.$bus.$off("program-updated");
  },

  methods: {
    async getPrograms(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      programService
        .getAllPrograms(defaultQuery)
        .then((res) => {
          this.programs = res;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      const query = {
        offset: 0,
        limit: this.pagination.limit,
      };

      if (this.searchInput && this.searchInput.trim()) {
        query.$text = {
          $search: this.searchInput,
        };
      }

      this.getPrograms(query);
    }, DEBOUNCE_MS_TIMEOUT),

    showProgramModal(program) {
      if (program) {
        this.selectedProgram = program;
      }
      this.isProgramModalVisible = true;
    },

    closeProgramModal() {
      this.isProgramModalVisible = false;
      this.selectedProgram = null;
    },

    deleteProgram(program) {
      this.$confirm(
        `¿Desea eliminar el programa: ${program.name}?`,
        "Eliminar",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          programService.deleteProgram(program._id);
          this.programs = this.programs.filter((p) => p._id !== program._id);
        })
        .catch(() => {});
    },

    onPageChange() {
      const query = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
      };

      if (this.searchInput && this.searchInput.trim()) {
        query.$text = {
          $search: this.searchInput,
        };
      }

      this.getPrograms(query);
    },

    setPagination(pagination) {
      this.pagination = { ...this.pagination, ...pagination };

      const query = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
      };

      if (this.searchInput && this.searchInput.trim()) {
        query.$text = {
          $search: this.searchInput,
        };
      }

      this.getPrograms(query);
    },
  },
};
</script>

<template lang="pug">
  section.program-list
    header.headline
      .headline__actions
        el-input.search__input(
          prefix-icon="el-icon-search"
          @input="debounceInput"
          v-model="searchInput"
          placeholder="Buscar"
          :clearable="true"
        )
        .new_program.button.button--blue.box__icon(@click='showProgramModal()' v-auth="['doctor.create', 'manager.create', 'assistan.create', 'admin.create']")
          micon(name="add_circle_outline").button__icon
          span.button__text Nuevo Programa
    .box
      .box__content--stretch
        table
          thead
            tr
              th Nombre
              th Descripción
              th Estado
              th Requiere Aprobación
              th Formularios
              th Acciones
          tbody
            spinner(v-if="isLoading")
            tr(v-else v-for="(program, index) in programs" :key="program._id" @click='showProgramModal(program)')
              td {{ program.name }}
              td {{ program.description }}
              td
                el-tag(
                  :type="program.isActive ? 'success' : 'danger'"
                  size="small"
                ) {{ program.isActive ? 'Activo' : 'Inactivo' }}
              td
                el-tag(
                  :type="program.requiresApproval ? 'warning' : 'danger'"
                  size="small"
                ) {{ program.requiresApproval ? 'Sí' : 'No' }}
              td
                span {{ program.initialData ? program.initialData.length : 0 }} formulario(s)
              td.actions
                .actions-container
                  el-dropdown(trigger="click")
                    .button.button--action.el-dropdown-link(@click.stop)
                      micon(name="more_horiz")
                    el-dropdown-menu(slot="dropdown")
                      el-dropdown-item
                          div(@click="showProgramModal(program)")
                            micon(name="edit")
                            | Editar programa
                      el-dropdown-item
                          div(@click="deleteProgram(program)")
                            micon(name="delete_forever")
                            | Eliminar programa

      ModalProgram(
        v-if='isProgramModalVisible'
        :current-program='selectedProgram'
        :show-dialog-modal='isProgramModalVisible' 
        @close='closeProgramModal'
      )

      pagination(
        :isLoading="isLoading"
        :limit="pagination.limit"
        :total="pagination.total"
        @pagination="setPagination"
      )

</template>

<style lang="scss" scoped>
@import "@/styles/settings/index.scss";
@import "@/styles/tools/index.scss";

tbody td:hover {
  cursor: pointer;
}

.box {
  height: 100%;
}

.program-list {
  height: 65vh;
  .headline {
    .headline__title {
      @include media(desktop-up) {
        margin: 0 15px 15px 0;
      }
      @include media(web-up) {
        margin: 0 15px 0 0;
      }
    }
    .headline__actions {
      @include media(mobile-up) {
        width: 100%;
      }
      @include media(web-up) {
        width: auto;
      }
    }
  }
}
</style>
